import { useState } from 'react';
import { useApiUsage } from '../lib/queries';
import { Chip, Empty, Loading, Pill, Tile } from '../components/ui';

/* What the apps and the console asked the server for, from the usage log the
   BFF writes per request (api_requests, kept 14 days). Built to answer the
   question that had no answer after 2026-10-23's field test: is this one phone,
   or every phone on one build? */

const RANGES: Array<{ h: number; label: string }> = [
  { h: 1, label: '1 h' }, { h: 24, label: '24 h' }, { h: 24 * 7, label: '7 d' }, { h: 24 * 14, label: '14 d' },
];

function when(at: string) {
  return new Date(at).toLocaleString(undefined, {
    day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
}

export function ApiUsage() {
  const [hours, setHours] = useState(24);
  const { data, isLoading, error } = useApiUsage(hours);

  if (isLoading) return <Loading />;
  if (error || !data) return <Empty title="The usage log is not available." />;

  const t = data.totals;
  const slowest = [...data.routes].sort((a, b) => b.p95 - a.p95).slice(0, 10);

  return (
    <>
      <div className="tiles">
        <Tile k="Requests" v={t.calls} d={`last ${RANGES.find((r) => r.h === hours)?.label ?? `${hours} h`}`} />
        <Tile k="Callers" v={t.callers} d="drivers, agents, console users" />
        <Tile k="Server errors" v={t.errors5xx} d="5xx responses" />
        <Tile k="Refused" v={t.errors4xx} d="4xx — expired sessions, bad input" />
      </div>

      <div className="toolbar">
        <div className="chips">
          {RANGES.map((r) => (
            <Chip key={r.h} label={r.label} selected={hours === r.h} onClick={() => setHours(r.h)} />
          ))}
        </div>
      </div>

      <h3 style={{ margin: '22px 0 8px', fontSize: 15 }}>App builds in use</h3>
      {!data.versions.length ? (
        <Empty title="No app requests in this window." />
      ) : (
        <div className="tbl">
          <table>
            <thead><tr><th>App</th><th>Build</th><th>Phones</th><th>Requests</th><th>Errors</th><th>Last seen</th></tr></thead>
            <tbody>
              {data.versions.map((v) => (
                <tr key={`${v.app}-${v.version}`}>
                  <td>{v.app}</td>
                  <td className="mono">{v.version}</td>
                  <td className="mono">{v.callers}</td>
                  <td className="mono">{v.calls}</td>
                  <td>{v.errors ? <Pill tone="mid">{v.errors}</Pill> : <span className="faint">0</span>}</td>
                  <td className="mono">{when(v.last_seen)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <h3 style={{ margin: '22px 0 8px', fontSize: 15 }}>Busiest routes</h3>
      <div className="tbl">
        <table>
          <thead><tr><th>Route</th><th>Calls</th><th>4xx</th><th>5xx</th><th>p50 ms</th><th>p95 ms</th></tr></thead>
          <tbody>
            {data.routes.slice(0, 25).map((r) => (
              <tr key={`${r.method} ${r.route}`}>
                <td className="mono">{r.method} {r.route}</td>
                <td className="mono">{r.calls}</td>
                <td className="mono">{r.errors4xx || <span className="faint">0</span>}</td>
                <td>{r.errors5xx ? <Pill tone="off">{r.errors5xx}</Pill> : <span className="faint">0</span>}</td>
                <td className="mono">{r.p50}</td>
                <td className="mono">{r.p95}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <h3 style={{ margin: '22px 0 8px', fontSize: 15 }}>Slowest routes (p95)</h3>
      <div className="tbl">
        <table>
          <thead><tr><th>Route</th><th>p95 ms</th><th>Max ms</th><th>Calls</th></tr></thead>
          <tbody>
            {slowest.map((r) => (
              <tr key={`slow-${r.method} ${r.route}`}>
                <td className="mono">{r.method} {r.route}</td>
                <td className="mono">{r.p95}</td>
                <td className="mono">{r.max_ms}</td>
                <td className="mono">{r.calls}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <h3 style={{ margin: '22px 0 8px', fontSize: 15 }}>Recent errors</h3>
      {!data.errors.length ? (
        <Empty title="No errors in this window." />
      ) : (
        <div className="tbl">
          <table>
            <thead><tr><th>When</th><th>Route</th><th>Status</th><th>Caller</th><th>Build</th><th>ms</th></tr></thead>
            <tbody>
              {data.errors.map((e, i) => (
                <tr key={i}>
                  <td className="mono" style={{ whiteSpace: 'nowrap' }}>{when(e.at)}</td>
                  <td className="mono">{e.method} {e.route}</td>
                  <td><Pill tone={e.status >= 500 ? 'off' : 'mid'}>{e.status}</Pill></td>
                  <td>{e.caller_kind ? <><div className="li-title">{e.caller_kind}</div><div className="li-sub mono">{e.caller_id}</div></> : <span className="faint">signed out</span>}</td>
                  <td className="mono">{e.app_version ?? <span className="faint">—</span>}</td>
                  <td className="mono">{e.ms}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="li-sub" style={{ marginTop: 9 }}>
        One row per request, kept 14 days. Driver builds from 57 name themselves on every request;
        older builds are recognised from their update check. The full server log is kept 30 days on the host.
      </div>
    </>
  );
}
